import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "../styles/LandingPageDesktop.css";
import api from "../lib/api";

export default function LandingPageDesktop() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const DESIGN_WIDTH = 1920; // Largura base do design desktop (Full HD)
  const [scale, setScale] = useState(1);
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    nomeCompleto: "",
    cpf: "",
    email: "",
    whatsapp: "",
    cidadeEstado: "",
    revenda: "",
    produto: "",
    quantidadeSacas: "",
    notaFiscal: null,
    aceitaTermos: false,
  });

  const [errors, setErrors] = useState({});

  const backgroundStyle = {
    backgroundImage: 'url(/assets/fundo-colorido 1.jpg)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top left',
    backgroundSize: 'cover'
  };

  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Função para scroll até o formulário
  const scrollToForm = () => {
    const inputElement = document.getElementById('nome-completo-input');
    if (inputElement) {
      inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Foca no campo após um pequeno delay para garantir que o scroll terminou
      setTimeout(() => {
        inputElement.focus();
      }, 500);
    }
  };

  // Formatação de CPF
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return value;
  };

  // Formatação de WhatsApp/Telefone
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
    }
    return value;
  };

  // Handler para mudanças nos campos
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === "file") {
      setFormData({ ...formData, notaFiscal: files[0] });
      return;
    }
    
    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
      return;
    }

    let formattedValue = value;
    if (name === "cpf") {
      formattedValue = formatCPF(value);
    } else if (name === "whatsapp") {
      formattedValue = formatPhone(value);
    }

    setFormData({ ...formData, [name]: formattedValue });
    // Remove erro do campo quando começa a digitar
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Validação do formulário
  const validateForm = () => {
    const newErrors = {};

    if (!formData.nomeCompleto.trim()) {
      newErrors.nomeCompleto = "Nome completo é obrigatório";
    }

    if (!formData.cpf.trim()) {
      newErrors.cpf = "CPF é obrigatório";
    } else if (formData.cpf.replace(/\D/g, "").length !== 11) {
      newErrors.cpf = "CPF deve ter 11 dígitos";
    }

    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = "WhatsApp é obrigatório";
    } else if (formData.whatsapp.replace(/\D/g, "").length < 10) {
      newErrors.whatsapp = "Telefone deve ter pelo menos 10 dígitos";
    }

    if (!formData.notaFiscal) {
      newErrors.notaFiscal = "Anexo da nota fiscal é obrigatório";
    }

    if (!formData.aceitaTermos) {
      newErrors.aceitaTermos = "Você deve aceitar os termos";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Por favor, corrija os erros antes de enviar.");
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("nome", formData.nomeCompleto);
      formDataToSend.append("cpf_cnpj", formData.cpf.replace(/\D/g, ""));
      formDataToSend.append("email", formData.email);
      formDataToSend.append("telefone", formData.whatsapp.replace(/\D/g, ""));
      formDataToSend.append("cidade_uf", formData.cidadeEstado);
      formDataToSend.append("revenda", formData.revenda);
      formDataToSend.append("produto", formData.produto);
      formDataToSend.append("qtde_sacas", formData.quantidadeSacas || "0");
      formDataToSend.append("arquivo", formData.notaFiscal);

      const response = await api.post("/participar", formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(response.data.message || "Cadastro realizado com sucesso!");
      
      // Limpa o formulário
      setFormData({
        nomeCompleto: "",
        cpf: "",
        email: "",
        whatsapp: "",
        cidadeEstado: "",
        revenda: "",
        produto: "",
        quantidadeSacas: "",
        notaFiscal: null,
        aceitaTermos: false,
      });
      
      // Limpa o input de arquivo
      const fileInput = document.getElementById("nota-fiscal");
      if (fileInput) fileInput.value = "";

    } catch (error) {
      const errorMessage = error.response?.data?.error || "Erro ao enviar formulário. Tente novamente.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Escala proporcional de toda a página conforme a largura da janela
  useEffect(() => {
    const updateScale = () => {
      const windowWidth = window.innerWidth;
      // Calcula a escala baseada na largura da janela
      // Limita a escala mínima para telas muito pequenas e máxima para telas muito grandes
      const calculatedScale = Math.min(Math.max(windowWidth / DESIGN_WIDTH, 0.5), 1.5);
      setScale(calculatedScale);
      
      // Remove espaço em branco criado pelo transform: scale()
      const removeWhiteSpace = () => {
        const wrapper = document.querySelector('.desktop-scale-wrapper');
        const container = document.querySelector('.landing-page-desktop');
        
        if (!wrapper || calculatedScale >= 1) {
          // Reset quando escala >= 1
          if (wrapper) {
            wrapper.style.marginBottom = '';
            wrapper.style.paddingBottom = '';
          }
          if (container) {
            container.style.maxHeight = '';
            container.style.overflow = '';
            container.style.paddingBottom = '';
            container.style.marginBottom = '';
          }
          return;
        }
        
        // Encontra o último elemento visível (footer ou última seção)
        const footer = wrapper.querySelector('.footer-section');
        if (!footer) return;
        
        // Calcula a altura total do wrapper
        const wrapperHeight = wrapper.scrollHeight;
        
        // Calcula onde o footer realmente termina dentro do wrapper
        const footerOffsetTop = footer.offsetTop;
        const footerHeight = footer.scrollHeight || footer.offsetHeight;
        const footerBottom = footerOffsetTop + footerHeight;
        
        // O espaço físico do wrapper é maior que o visual devido ao scale
        // Quando scale = 0.8, o visual é 80%, mas o espaço físico é 100%
        // O espaço extra criado pelo scale é: altura_total * (1 - scale)
        const spaceFromScale = wrapperHeight * (1 - calculatedScale);
        
        // O espaço abaixo do footer também precisa ser removido
        const spaceBelowFooter = wrapperHeight - footerBottom;
        
        // Total de espaço a remover: espaço do scale + espaço abaixo do footer
        // Mas preciso ajustar porque o scale também afeta o espaço abaixo do footer
        const totalSpaceToRemove = spaceFromScale + (spaceBelowFooter * calculatedScale);
        
        // Aplica margin-bottom negativo
        wrapper.style.marginBottom = `-${Math.round(totalSpaceToRemove)}px`;
        wrapper.style.paddingBottom = '0';
        
        // Ajusta o container para altura mínima mas sem espaço extra
        if (container) {
          container.style.paddingBottom = '0';
          container.style.marginBottom = '0';
          // Não limita altura para não cortar conteúdo
        }
        
        // Remove padding/margin do body e html
        document.body.style.paddingBottom = '0';
        document.body.style.marginBottom = '0';
        document.documentElement.style.paddingBottom = '0';
        document.documentElement.style.marginBottom = '0';
      };
      
      // Executa em vários momentos para garantir precisão
      removeWhiteSpace();
      setTimeout(removeWhiteSpace, 200);
      setTimeout(removeWhiteSpace, 500);
      window.addEventListener('load', removeWhiteSpace);
    };
    
    // Executa imediatamente e no resize
    updateScale();
    window.addEventListener("resize", updateScale);
    
    // Também executa após o conteúdo carregar
    window.addEventListener("load", updateScale);
    
    return () => {
      window.removeEventListener("resize", updateScale);
      window.removeEventListener("load", updateScale);
    };
  }, []);

  return (
    <div className="landing-page-desktop" style={backgroundStyle}>
      <div
        className="desktop-scale-wrapper"
        style={{
          width: DESIGN_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
      {/* SEÇÃO PRINCIPAL */}
      <section className="main-container">
        {/* Fundo com duas imagens sobrepostas */}
        <div className="background-wrapper">
          <img
            src="/assets/fgbgf 400.jpg"
            alt="Fundo principal"
            className="bg-base"
          />
          <img
            src="/assets/Prancheta 10.png"
            alt="Sobreposição"
            className="bg-overlay"
          />
        </div>

        {/* Conteúdo principal sobre o fundo */}
        <div className="content-wrapper">
          {/* LADO ESQUERDO - HERO */}
          <div className="hero-side">
            <div className="hero-content">
              
              
            </div>
          </div>

          {/* LADO DIREITO - FORMULÁRIO */}
          <div className="form-side">
            <div className="form-container">
              <form className="registration-form" onSubmit={handleSubmit}>
                {/* NOME */}
                <div className="form-field">
                  <label className="field-label">NOME COMPLETO*</label>
                  <input
                    type="text"
                    id="nome-completo-input"
                    name="nomeCompleto"
                    className={`field-input ${errors.nomeCompleto ? "error" : ""}`}
                    placeholder="Nome Completo"
                    value={formData.nomeCompleto}
                    onChange={handleChange}
                    required
                  />
                  {errors.nomeCompleto && <span className="error-message">{errors.nomeCompleto}</span>}
                </div>

                {/* CPF */}
                <div className="form-field">
                  <label className="field-label">CPF*</label>
                  <input
                    type="text"
                    name="cpf"
                    className={`field-input ${errors.cpf ? "error" : ""}`}
                    placeholder="000.000.000-00"
                    value={formData.cpf}
                    onChange={handleChange}
                    maxLength="14"
                    required
                  />
                  {errors.cpf && <span className="error-message">{errors.cpf}</span>}
                </div>

                {/* E-MAIL */}
                <div className="form-field">
                  <label className="field-label">E-MAIL*</label>
                  <input
                    type="email"
                    name="email"
                    className={`field-input ${errors.email ? "error" : ""}`}
                    placeholder="nome@nome.com.br"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                {/* WHATSAPP */}
                <div className="form-field">
                  <label className="field-label">WHATSAPP*</label>
                  <input
                    type="tel"
                    name="whatsapp"
                    className={`field-input ${errors.whatsapp ? "error" : ""}`}
                    placeholder="(00) 00000-0000"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    maxLength="15"
                    required
                  />
                  {errors.whatsapp && <span className="error-message">{errors.whatsapp}</span>}
                </div>

                {/* CIDADE / ESTADO */}
                <div className="form-field">
                  <label className="field-label">CIDADE / ESTADO</label>
                  <input
                    type="text"
                    name="cidadeEstado"
                    className="field-input"
                    placeholder="Cidade/UF"
                    value={formData.cidadeEstado}
                    onChange={handleChange}
                  />
                </div>

                {/* REVENDA */}
                <div className="form-field">
                  <label className="field-label">
                    REVENDA, AGROPECUÁRIA OU COOPERATIVA
                  </label>
                  <input
                    type="text"
                    name="revenda"
                    className="field-input"
                    placeholder="Nome"
                    value={formData.revenda}
                    onChange={handleChange}
                  />
                </div>

                {/* PRODUTO */}
                <div className="form-field">
                  <label className="field-label">
                    PRODUTO ADQUIRIDO (RX-TROPEIRO / RX-SUPREMO)
                  </label>
                  <input
                    type="text"
                    name="produto"
                    className="field-input"
                    placeholder="Produto"
                    value={formData.produto}
                    onChange={handleChange}
                  />
                </div>

                {/* QUANTIDADE */}
                <div className="form-field">
                  <label className="field-label">QUANTIDADE DE SACAS</label>
                  <input
                    type="number"
                    name="quantidadeSacas"
                    className="field-input"
                    placeholder="000"
                    value={formData.quantidadeSacas}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                {/* UPLOAD */}
                <div className="form-field">
                  <label className="field-label">
                    UPLOAD DA FOTO DA NOTA FISCAL*
                  </label>
                  <div className="file-upload">
                    <input
                      type="file"
                      id="nota-fiscal"
                      name="notaFiscal"
                      className={`file-input ${errors.notaFiscal ? "error" : ""}`}
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleChange}
                      required
                    />
                    <label htmlFor="nota-fiscal" className="file-label">
                      {formData.notaFiscal ? formData.notaFiscal.name : "Anexar Documento"}
                    </label>
                  </div>
                  {errors.notaFiscal && <span className="error-message">{errors.notaFiscal}</span>}
                </div>

                {/* CHECKBOX TERMOS */}
                <div className="form-field">
                  <label className={`terms-checkbox ${errors.aceitaTermos ? "error" : ""}`}>
                    <input 
                      type="checkbox" 
                      name="aceitaTermos"
                      checked={formData.aceitaTermos}
                      onChange={handleChange}
                      required 
                    />
                    <span>CONCORDO COM OS <button type="button" onClick={openModal} className="terms-link">TERMOS</button></span>
                  </label>
                  {errors.aceitaTermos && <span className="error-message">{errors.aceitaTermos}</span>}
                </div>

                {/* BOTÃO */}
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "ENVIANDO..." : "CADASTRAR AGORA"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* SEÇÃO SOBRE */}
      <section className="sobre-section">
        <img
          src="/assets/SOBRE.png"
          alt="Sobre a campanha"
          className="sobre-image"
        />
      </section>

      {/* SEÇÃO COMO FUNCIONA */}
      <section className="como-funciona-section">
        <img
          src="/assets/COMO VAI FUNCIONAR.jpg"
          alt="Como funciona a campanha"
          className="como-funciona-image"
        />
      </section>

      {/* SEÇÃO DFBCFGB */}
      <section className="dfbcfgb-section">
        <img
          src="/assets/DFBCFGB 1.png"
          alt="Elemento decorativo"
          className="dfbcfgb-image"
        />
      </section>

      {/* SEÇÃO SACAS */}
      <section className="sacas-section">
        <img
          src="/assets/SACAS.png"
          alt="Produtos Raíx"
          className="sacas-image"
        />
        {/* Botão CADASTRAR AGORA sobreposto */}
        <button 
          className="sacas-button"
          onClick={scrollToForm}
          aria-label="Ir para o formulário de cadastro"
        >
          CADASTRAR AGORA
        </button>
      </section>

      {/* SEÇÃO BENEFICIOS */}
      <section className="beneficios-section">
        <img
          src="/assets/BENEFICIOS LINHA.png"
          alt="Benefícios dos produtos"
          className="beneficios-image"
        />
      </section>

      {/* SEÇÃO REGULAMENTO */}
      <section className="regulamento-section">
        <img
          src="/assets/REGULAMENTO.png"
          alt="Regulamento da campanha"
          className="regulamento-image"
        />
      </section>

      {/* SEÇÃO PERNA */}
      <section className="perna-section">
        <img
          src="/assets/PERNA 3X.png"
          alt="Elemento decorativo"
          className="perna-image"
        />
      </section>

      {/* SEÇÃO FOOTER */}
      <section className="footer-section">
        <img
          src="/assets/FOOTER.png"
          alt="Footer Raíx"
          className="footer-image"
        />
        {/* Ícones de Redes Sociais */}
        <div className="social-icons-desktop">
          <a 
            href="https://www.facebook.com/raixbiosolucoes" 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-icon-desktop facebook-icon"
            aria-label="Facebook Raíx Biosoluções"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          <a 
            href="https://www.instagram.com/raixbiosolucoes" 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-icon-desktop instagram-icon"
            aria-label="Instagram Raíx Biosoluções"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a 
            href="https://www.linkedin.com/company/raixbiosolucoes" 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-icon-desktop linkedin-icon"
            aria-label="LinkedIn Raíx Biosoluções"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <a 
            href="https://www.youtube.com/channel/UCevxAfTVgt9Or7w4uI9HI7g" 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-icon-desktop youtube-icon"
            aria-label="YouTube Raíx Biosoluções"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
        </div>
      </section>
      </div>

      {/* MODAL DE TERMOS */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <h2>Proteção de Dados</h2>
            <div className="modal-text">
              <p>
                Os dados coletados para participação da campanha serão utilizados no legítimo interesse da parte promovente, para cumprimento das finalidades da Campanha Raíx Nutrição Animal 2025/2026. A Raíx se compromete em manter a correta conservação dos dados pessoais, na vigência e após o término da campanha para cumprimento de obrigação legal ou regulatória, respeitando os prazos legais, civis, previdenciários e fiscais para guarda de tais dados, nos termos do art. 16, I, da Lei 13.079/2018.
              </p>
              <p>
                O participante autoriza a utilização pela Raíx de sua imagem e voz, de forma gratuita, exclusivamente para fins de divulgação da campanha e dos seus resultados, de forma gratuita, em qualquer meio de comunicação social, público ou privado.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

